import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  BellRing,
  BriefcaseBusiness,
  CalendarDays,
  Camera,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock3,
  CircleUserRound,
  CloudUpload,
  CircleDollarSign,
  FileCheck2,
  Grid2X2,
  Image,
  Languages,
  LayoutDashboard,
  LoaderCircle,
  LockKeyhole,
  LogIn,
  LogOut,
  MapPin,
  MapPinned,
  Radio,
  RefreshCw,
  Send,
  ShieldCheck,
  UploadCloud,
  Users,
  UserPlus,
} from "lucide-react";
import {
  loadGallery,
  loadPhotographerApplication,
  loadWorkspace,
  requestOtp,
  rotateEventAccess,
  savePhotographerApplication,
  signOut,
  submitPhotographerApplication,
  submitPublicApplication,
  photographerMediaApi,
  getPhotographerPhotoDelivery,
  verifyOtp,
  exchangeAuthHandoff,
  registerForEvent,
} from "./api";
import {
  getPhotographerViewFromUrl,
  navigatePhotographerView,
  getPhotographerAuthModeFromUrl,
  navigatePhotographerAuthMode,
  navigatePhotographerWorkspace,
} from "./routes";
import { captureOtpFill, showDevOtpFill } from "./otp-fill";
import BulkMediaUploader from "./BulkMediaUploader";
import PhotographerGalleryManager from "./PhotographerGalleryManager";
import "./dynamic.css";

const navigation = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "events", label: "Events", icon: CalendarDays },
  { id: "uploads", label: "Upload", icon: UploadCloud },
  { id: "gallery", label: "Gallery", icon: Grid2X2 },
  { id: "earnings", label: "Earnings", icon: CircleDollarSign },
];

const specialties = [
  ["wedding", "Weddings"],
  ["engagement", "Engagements"],
  ["birthday", "Birthdays"],
  ["corporate", "Corporate"],
  ["religious", "Cultural & religious"],
  ["concert", "Concerts & live events"],
  ["sports", "Sports"],
  ["fashion", "Fashion & portfolio"],
];

function captureAuthHandoff(
  location = globalThis.location,
  history = globalThis.history,
) {
  const fragment = new URLSearchParams(
    String(location?.hash || "").replace(/^#/, ""),
  );
  if (fragment.get("workspace") !== "photographer") return "";
  const token = fragment.get("auth_handoff") || "";
  if (!token) return "";
  history?.replaceState?.(
    null,
    "",
    `${location.pathname || "/"}${location.search || ""}`,
  );
  return token;
}

function App() {
  const handoffTokenRef = useRef(captureAuthHandoff());
  const [otpFill, setOtpFill] = useState(() => captureOtpFill("user"));
  const [isPublicApplyEntry] = useState(
    () => getPhotographerAuthModeFromUrl() === "apply",
  );
  const [isExplicitSignInEntry] = useState(() =>
    ["/signin", "/login"].some((path) =>
      String(globalThis.location?.pathname || "")
        .toLowerCase()
        .includes(path),
    ),
  );
  const [application, setApplication] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [authRequired, setAuthRequired] = useState(
    isPublicApplyEntry || isExplicitSignInEntry,
  );
  const [loading, setLoading] = useState(
    !(isPublicApplyEntry || isExplicitSignInEntry),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [otpEmail, setOtpEmail] = useState(otpFill?.email || "");
  const [devCode, setDevCode] = useState("");

  useEffect(() => {
    function handleOtpHash() {
      const fill = captureOtpFill("user");
      if (fill) {
        setOtpFill(fill);
        setOtpEmail(fill.email);
        setAuthRequired(true);
      }
    }
    window.addEventListener("hashchange", handleOtpHash);
    window.addEventListener("focus", handleOtpHash);
    return () => {
      window.removeEventListener("hashchange", handleOtpHash);
      window.removeEventListener("focus", handleOtpHash);
    };
  }, []);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      if (handoffTokenRef.current) {
        const token = handoffTokenRef.current;
        handoffTokenRef.current = "";
        await exchangeAuthHandoff(token);
      }
      const nextApplication = await loadPhotographerApplication();
      setApplication(nextApplication);
      setAuthRequired(false);
      if (nextApplication.profile?.status === "approved") {
        setWorkspace(await loadWorkspace());
      } else {
        setWorkspace(null);
      }
    } catch (requestError) {
      setApplication(null);
      setWorkspace(null);
      setAuthRequired(
        requestError.status === 401 || requestError.status === 403,
      );
      setError(
        requestError.status === 401 || requestError.status === 403
          ? ""
          : requestError.message,
      );
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // The application form is public and does not need session discovery. Avoid
    // blocking it on a backend cold start; authenticated data is loaded after
    // sign-in, or immediately when the portal is opened in sign-in mode.
    if (isPublicApplyEntry || isExplicitSignInEntry) return undefined;
    refresh();
    return undefined;
  }, [isExplicitSignInEntry, isPublicApplyEntry, refresh]);

  useEffect(() => {
    if (application?.profile?.status !== "approved") return undefined;
    const timer = globalThis.setInterval(
      () => refresh({ silent: true }),
      15000,
    );
    return () => globalThis.clearInterval(timer);
  }, [application?.profile?.status, refresh]);

  async function sendCode(email) {
    setBusy(true);
    setError("");
    try {
      const data = await requestOtp(email);
      setOtpEmail(email);
      if (data?.devCode) setDevCode(data.devCode);
      return true;
    } catch (requestError) {
      setError(requestError.message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function authenticate(code) {
    setBusy(true);
    setError("");
    try {
      await verifyOtp(otpEmail, code);
      navigatePhotographerWorkspace("overview");
      await refresh();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function logout() {
    setBusy(true);
    await signOut().catch(() => undefined);
    setApplication(null);
    setWorkspace(null);
    setOtpEmail("");
    setDevCode("");
    setAuthRequired(true);
    navigatePhotographerAuthMode("signin");
    setBusy(false);
  }

  async function saveDraft(values) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const nextApplication = await savePhotographerApplication(values);
      setApplication(nextApplication);
      setNotice("Your application draft has been saved.");
      return true;
    } catch (requestError) {
      setError(requestError.message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function submit(values) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await savePhotographerApplication(values);
      const nextApplication = await submitPhotographerApplication();
      setApplication(nextApplication);
      setNotice("Your application was submitted for Admin review.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <Status icon={LoaderCircle} spin title="Loading Photographer portal" />
    );
  }
  if (!application && authRequired) {
    return (
      <AuthScreen
        busy={busy}
        error={error}
        otpEmail={otpEmail}
        initialCode={otpFill?.code || ""}
        fillCode={otpFill?.code || ""}
        devCode={devCode}
        sendCode={sendCode}
        verify={authenticate}
        reset={() => {
          setOtpEmail("");
          setDevCode("");
          setError("");
          setOtpFill(null);
        }}
      />
    );
  }
  if (!application) {
    return (
      <Status
        icon={Camera}
        title="Photographer portal unavailable"
        message={error}
        action={refresh}
      />
    );
  }
  if (application.profile?.status === "approved" && workspace) {
    return (
      <Workspace workspace={workspace} logout={logout} refresh={refresh} />
    );
  }

  return (
    <ApplicationPortal
      application={application}
      busy={busy}
      error={error}
      notice={notice}
      logout={logout}
      saveDraft={saveDraft}
      submit={submit}
    />
  );
}

function AuthScreen({
  busy,
  error,
  otpEmail,
  initialCode,
  fillCode,
  devCode,
  sendCode,
  verify,
  reset,
  initialMode,
}) {
  const [mode, setMode] = useState(
    () => initialMode || getPhotographerAuthModeFromUrl(),
  );
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(initialCode || fillCode || "");
  const awaitingCode = Boolean(otpEmail);

  useEffect(() => {
    if (fillCode) {
      setCode(fillCode);
    }
  }, [fillCode]);

  const [applyForm, setApplyForm] = useState(() => ({
    displayName: "",
    email: "",
    phone: "",
    businessName: "",
    headline: "",
    bio: "",
    experienceYears: "",
    primaryCity: "",
    serviceRadiusKm: "",
    specialtyCodes: [],
    portfolioUrls: "",
    equipment: "",
    languages: "English",
    minimum: "",
    maximum: "",
    currency: "INR",
  }));
  const [applyDeclaration, setApplyDeclaration] = useState(false);
  const [applySubmitting, setApplySubmitting] = useState(false);
  const [applyError, setApplyError] = useState("");
  const [submittedResult, setSubmittedResult] = useState(null);

  useEffect(() => {
    function onPopState() {
      setMode(getPhotographerAuthModeFromUrl());
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  function switchMode(nextMode) {
    if (nextMode === mode) return;
    setMode(nextMode);
    navigatePhotographerAuthMode(nextMode);
    setApplyError("");
  }

  const isApply = mode === "apply";

  const setField = (field) => (value) =>
    setApplyForm((current) => ({ ...current, [field]: value }));

  const toggleSpecialty = (specialtyCode) =>
    setApplyForm((current) => ({
      ...current,
      specialtyCodes: current.specialtyCodes.includes(specialtyCode)
        ? current.specialtyCodes.filter((item) => item !== specialtyCode)
        : [...current.specialtyCodes, specialtyCode],
    }));

  async function handleApplySubmit(event) {
    event.preventDefault();
    setApplyError("");
    if (!applyDeclaration) {
      setApplyError(
        "Please accept the accuracy declaration before submitting.",
      );
      return;
    }
    const portfolioList = lines(applyForm.portfolioUrls);
    if (!portfolioList.length) {
      setApplyError("Please provide at least one valid portfolio URL.");
      return;
    }
    if (portfolioList.some((value) => !isHttpUrl(value))) {
      setApplyError("Each portfolio link must be a valid HTTP or HTTPS URL.");
      return;
    }
    if (applyForm.specialtyCodes.length === 0) {
      setApplyError("Please select at least one photography specialty.");
      return;
    }
    if (
      applyForm.minimum !== "" &&
      applyForm.maximum !== "" &&
      Number(applyForm.minimum) > Number(applyForm.maximum)
    ) {
      setApplyError("Minimum price cannot be greater than maximum price.");
      return;
    }
    setApplySubmitting(true);
    try {
      const payload = {
        email: applyForm.email.trim().toLowerCase(),
        displayName: applyForm.displayName.trim(),
        phone: applyForm.phone.trim(),
        businessName: applyForm.businessName.trim(),
        headline: applyForm.headline.trim(),
        bio: applyForm.bio.trim(),
        experienceYears: Number(applyForm.experienceYears),
        primaryCity: applyForm.primaryCity.trim(),
        serviceRadiusKm: Number(applyForm.serviceRadiusKm),
        specialtyCodes: applyForm.specialtyCodes,
        portfolioUrls: portfolioList,
        equipment: commaList(applyForm.equipment),
        languages: commaList(applyForm.languages),
        pricingRange: {
          minimum: Number(applyForm.minimum),
          maximum: Number(applyForm.maximum),
          currency: applyForm.currency,
        },
        declarationAccepted: true,
      };
      const result = await submitPublicApplication(payload);
      setSubmittedResult(result);
    } catch (requestError) {
      setApplyError(
        requestError.message ||
          "Failed to submit application. Please try again.",
      );
    } finally {
      setApplySubmitting(false);
    }
  }

  return (
    <main className={`partner-auth ${isApply ? "apply-mode" : ""}`}>
      <section className="partner-auth-story">
        <Brand light />
        <div>
          <span className="partner-kicker">
            {isApply ? "Create meaningful work" : "Partner Workspace"}
          </span>
          <h1>
            {isApply
              ? "Become a trusted PhotoX photography partner."
              : "Welcome back to your photography studio."}
          </h1>
          <p>
            {isApply
              ? "Build your professional profile, submit your portfolio and join events after Admin approval."
              : "Access your upcoming events, upload high-resolution media and manage your client galleries."}
          </p>
        </div>
        <ul>
          <li>
            <ShieldCheck /> Verified partner network
          </li>
          <li>
            <CalendarDays /> Relevant event opportunities
          </li>
          <li>
            <BadgeCheck /> Transparent approval status
          </li>
        </ul>
      </section>

      {isApply ? (
        submittedResult ? (
          <section className="partner-auth-card apply-mode partner-auth-success">
            <Brand />
            <div className="partner-status-icon status-submitted">
              <CheckCircle2 size={36} />
            </div>
            <p className="eyebrow">Application received</p>
            <h2>Your application is in the review queue!</h2>
            <p className="partner-muted">
              Thank you,{" "}
              <strong>
                {submittedResult.user?.displayName || applyForm.displayName}
              </strong>
              . Your profile for{" "}
              <strong>
                {submittedResult.profile?.businessName ||
                  applyForm.businessName}
              </strong>{" "}
              has been submitted to the PhotoX Admin team for verification.
            </p>
            <div className="partner-success-summary">
              <div>
                <small>Applicant</small>
                <strong>
                  {submittedResult.user?.displayName} (
                  {submittedResult.user?.email})
                </strong>
              </div>
              <div>
                <small>Studio</small>
                <strong>{submittedResult.profile?.businessName}</strong>
              </div>
              <div>
                <small>Primary City</small>
                <strong>
                  {submittedResult.profile?.primaryCity} (
                  {submittedResult.profile?.serviceRadiusKm} km radius)
                </strong>
              </div>
              <div>
                <small>Specialties</small>
                <strong>
                  {(submittedResult.profile?.specialtyCodes || []).join(", ")}
                </strong>
              </div>
              <div>
                <small>Status</small>
                <span className="partner-status-pill status-submitted">
                  Submitted for review
                </span>
              </div>
            </div>
            <p className="partner-next-steps">
              Once approved by the Admin team, you will receive full partner
              access. You can sign in using this email anytime.
            </p>
            <div className="partner-form-actions">
              <button
                type="button"
                className="button button-primary"
                onClick={() => {
                  setEmail(submittedResult.user?.email || applyForm.email);
                  setSubmittedResult(null);
                  switchMode("signin");
                }}
              >
                <LogIn size={16} /> Sign in
              </button>
            </div>
          </section>
        ) : (
          <form
            className="partner-auth-card apply-mode"
            onSubmit={handleApplySubmit}
          >
            <Brand />
            <p className="eyebrow">Photographer partner portal</p>
            <div
              className="partner-auth-tabs"
              role="tablist"
              aria-label="Authentication mode"
            >
              <button
                type="button"
                role="tab"
                aria-selected={false}
                className="partner-auth-tab"
                onClick={() => switchMode("signin")}
              >
                <LogIn size={15} /> Sign in
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={true}
                className="partner-auth-tab active"
                onClick={() => switchMode("apply")}
              >
                <UserPlus size={15} /> Apply here
              </button>
            </div>
            <h2>Start your application</h2>
            <p className="partner-muted">
              Tell us about your photography studio, experience, specialties,
              and work samples.
            </p>
            {(applyError || error) && (
              <Notice error text={applyError || error} />
            )}

            <FormSection
              icon={CircleUserRound}
              title="Contact details"
              text="How the PhotoX team will identify and contact you."
            >
              <div className="partner-field-grid two">
                <Field
                  label="Full name *"
                  value={applyForm.displayName}
                  onChange={setField("displayName")}
                  required
                  autoComplete="name"
                  placeholder="e.g. Yash Sharma"
                />
                <Field
                  label="Phone number *"
                  type="tel"
                  value={applyForm.phone}
                  onChange={setField("phone")}
                  required
                  autoComplete="tel"
                  placeholder="+91 90000 00000"
                />
              </div>
              <Field
                label="Email address *"
                type="email"
                value={applyForm.email}
                onChange={setField("email")}
                required
                autoComplete="email"
                placeholder="you@studio.com"
                hint="You will use this email to sign in once approved."
              />
            </FormSection>

            <FormSection
              icon={BriefcaseBusiness}
              title="Professional profile"
              text="Present your studio and experience clearly to the review team."
            >
              <div className="partner-field-grid two">
                <Field
                  label="Business or studio name *"
                  value={applyForm.businessName}
                  onChange={setField("businessName")}
                  required
                  placeholder="Yash Visuals / Moments Studio"
                />
                <Field
                  label="Years of experience *"
                  type="number"
                  min="0"
                  max="80"
                  value={applyForm.experienceYears}
                  onChange={setField("experienceYears")}
                  required
                  placeholder="5"
                />
              </div>
              <Field
                label="Professional headline *"
                value={applyForm.headline}
                onChange={setField("headline")}
                minLength="10"
                maxLength="180"
                required
                placeholder="Wedding and destination celebration photographer"
                hint={`${applyForm.headline.length}/180 · minimum 10 characters`}
              />
              <TextArea
                label="Professional biography *"
                value={applyForm.bio}
                onChange={setField("bio")}
                minLength="50"
                maxLength="3000"
                required
                placeholder="Describe your photography style, background, philosophy, and experience..."
                hint={`${applyForm.bio.length}/3000 · minimum 50 characters`}
              />
            </FormSection>

            <FormSection
              icon={MapPin}
              title="Service area"
              text="Help Admin match you with relevant events."
            >
              <div className="partner-field-grid two">
                <Field
                  label="Primary city *"
                  value={applyForm.primaryCity}
                  onChange={setField("primaryCity")}
                  required
                  placeholder="e.g. Mumbai, Delhi, Hyderabad"
                />
                <Field
                  label="Service radius (km) *"
                  type="number"
                  min="0"
                  max="5000"
                  value={applyForm.serviceRadiusKm}
                  onChange={setField("serviceRadiusKm")}
                  required
                  placeholder="100"
                />
              </div>
            </FormSection>

            <FormSection
              icon={Camera}
              title="Specialties and portfolio"
              text="Choose your strongest event categories and provide real work samples."
            >
              <fieldset className="partner-specialties">
                <legend>Photography specialties *</legend>
                <div>
                  {specialties.map(([code, label]) => (
                    <label
                      key={code}
                      className={
                        applyForm.specialtyCodes.includes(code)
                          ? "selected"
                          : ""
                      }
                    >
                      <input
                        type="checkbox"
                        checked={applyForm.specialtyCodes.includes(code)}
                        onChange={() => toggleSpecialty(code)}
                      />
                      <CheckCircle2 size={17} />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>
              <TextArea
                label="Portfolio URLs *"
                value={applyForm.portfolioUrls}
                onChange={setField("portfolioUrls")}
                required
                placeholder="https://yourportfolio.com/weddings&#10;https://instagram.com/yourstudio"
                hint="Enter one HTTP or HTTPS URL per line."
              />
              <Field
                label="Camera and lighting equipment"
                value={applyForm.equipment}
                onChange={setField("equipment")}
                placeholder="Sony A7 IV, 24–70mm f/2.8, Godox AD200"
                hint="Separate items with commas."
              />
            </FormSection>

            <FormSection
              icon={Languages}
              title="Languages and pricing"
              text="Provide an indicative range; final event pricing can be agreed separately."
            >
              <Field
                label="Languages spoken *"
                value={applyForm.languages}
                onChange={setField("languages")}
                required
                placeholder="English, Hindi, Telugu"
                hint="Separate languages with commas."
              />
              <div className="partner-field-grid three">
                <Field
                  label="Minimum price *"
                  type="number"
                  min="0"
                  value={applyForm.minimum}
                  onChange={setField("minimum")}
                  required
                  placeholder="15000"
                />
                <Field
                  label="Maximum price *"
                  type="number"
                  min="0"
                  value={applyForm.maximum}
                  onChange={setField("maximum")}
                  required
                  placeholder="80000"
                />
                <label className="partner-field">
                  Currency *
                  <select
                    value={applyForm.currency}
                    onChange={(event) =>
                      setField("currency")(event.target.value)
                    }
                  >
                    <option value="INR">INR (₹)</option>
                    <option value="USD">USD ($)</option>
                    <option value="AED">AED (AED)</option>
                  </select>
                </label>
              </div>
            </FormSection>

            <label className="partner-declaration">
              <input
                type="checkbox"
                checked={applyDeclaration}
                onChange={(event) => setApplyDeclaration(event.target.checked)}
              />
              <span>
                <strong>
                  I confirm that this information and portfolio are accurate.
                </strong>
                <small>
                  I understand that PhotoX may reject or suspend profiles that
                  contain misleading information.
                </small>
              </span>
            </label>

            <div className="partner-form-actions">
              <button
                type="submit"
                className="button button-primary"
                disabled={applySubmitting || !applyDeclaration}
              >
                {applySubmitting ? (
                  <LoaderCircle className="spin" size={17} />
                ) : (
                  <Send size={17} />
                )}
                {applySubmitting
                  ? "Submitting application..."
                  : "Submit application for review"}
              </button>
            </div>

            <div className="partner-auth-switch">
              <span>Already have an account or applied?</span>
              <button
                type="button"
                className="partner-auth-switch-btn"
                onClick={() => switchMode("signin")}
              >
                Sign in
              </button>
            </div>

            <small className="partner-privacy">
              By continuing, you agree to use PhotoX responsibly and keep event
              content private.
            </small>
          </form>
        )
      ) : (
        <form
          className="partner-auth-card"
          onSubmit={async (event) => {
            event.preventDefault();
            if (awaitingCode) await verify(code);
            else await sendCode(email);
          }}
        >
          <Brand />
          <p className="eyebrow">Photographer partner portal</p>
          {!awaitingCode && (
            <div
              className="partner-auth-tabs"
              role="tablist"
              aria-label="Authentication mode"
            >
              <button
                type="button"
                role="tab"
                aria-selected={true}
                className="partner-auth-tab active"
                onClick={() => switchMode("signin")}
              >
                <LogIn size={15} /> Sign in
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={false}
                className="partner-auth-tab"
                onClick={() => switchMode("apply")}
              >
                <UserPlus size={15} /> Apply here
              </button>
            </div>
          )}
          <h2>
            {awaitingCode ? "Check your email" : "Sign in to your account"}
          </h2>
          <p className="partner-muted">
            {awaitingCode
              ? `Enter the one-time code sent to ${otpEmail}.`
              : "Sign in securely with your email to access your workspace."}
          </p>
          {error && <Notice error text={error} />}
          {fillCode && awaitingCode && (
            <button
              type="button"
              className="dev-otp-btn"
              onClick={() => setCode(fillCode)}
              title="Tap to fill OTP from magic link"
            >
              <span>
                Magic link OTP: <strong>{fillCode}</strong>
              </span>
              <small>Tap to fill</small>
            </button>
          )}
          {showDevOtpFill(devCode) && !fillCode && awaitingCode && (
            <button
              type="button"
              className="dev-otp-btn"
              onClick={() => {
                setCode(devCode);
              }}
              title="Tap to fill the same OTP emailed by SES (testing only)"
            >
              <span>
                Testing OTP: <strong>{devCode}</strong>
              </span>
              <small>{code === devCode ? "✓ Filled" : "Tap to fill"}</small>
            </button>
          )}
          {awaitingCode ? (
            <Field
              label="One-time code"
              value={code}
              onChange={(value) =>
                setCode(value.replace(/\D/g, "").slice(0, 8))
              }
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="Enter 6-digit code"
              required
            />
          ) : (
            <Field
              label="Email address"
              type="email"
              value={email}
              onChange={setEmail}
              autoComplete="email"
              placeholder="you@studio.com"
              required
            />
          )}
          <button
            className="button button-dark partner-primary-action"
            disabled={busy || (awaitingCode && code.length < 6)}
          >
            {busy ? (
              <LoaderCircle className="spin" size={18} />
            ) : (
              <LockKeyhole size={18} />
            )}
            {awaitingCode ? "Verify and continue" : "Send secure OTP"}
          </button>
          {awaitingCode ? (
            <button type="button" className="partner-link" onClick={reset}>
              Use another email
            </button>
          ) : (
            <div className="partner-auth-switch">
              <span>New photographer partner?</span>
              <button
                type="button"
                className="partner-auth-switch-btn"
                onClick={() => switchMode("apply")}
              >
                Apply here
              </button>
            </div>
          )}
          <small className="partner-privacy">
            By continuing, you agree to use PhotoX responsibly and keep event
            content private.
          </small>
        </form>
      )}
    </main>
  );
}

function ApplicationPortal({
  application,
  busy,
  error,
  notice,
  logout,
  saveDraft,
  submit,
}) {
  const status = application.profile?.status || "draft";
  const editable = ["draft", "rejected"].includes(status);
  return (
    <main className="partner-portal">
      <header className="partner-header">
        <Brand />
        <div className="partner-header-user">
          <span>{application.user.email}</span>
          <button onClick={logout} disabled={busy}>
            <LogOut size={17} /> Sign out
          </button>
        </div>
      </header>
      <div className="partner-progress" aria-label="Application progress">
        <ProgressStep
          active
          complete={Boolean(application.profile)}
          label="Profile"
        />
        <ProgressStep
          active={status !== "draft"}
          complete={["under_review", "approved"].includes(status)}
          label="Submit"
        />
        <ProgressStep
          active={["under_review", "approved"].includes(status)}
          complete={status === "approved"}
          label="Review"
        />
        <ProgressStep
          active={status === "approved"}
          complete={status === "approved"}
          label="Partner"
        />
      </div>
      <div className="partner-page">
        {error && <Notice error text={error} />}
        {notice && <Notice text={notice} />}
        {!editable ? (
          <ApplicationStatus application={application} />
        ) : (
          <ApplicationForm
            application={application}
            busy={busy}
            saveDraft={saveDraft}
            submit={submit}
          />
        )}
      </div>
    </main>
  );
}

function ProgressStep({ active, complete, label }) {
  return (
    <span className={active ? "active" : ""}>
      <i>{complete ? <Check size={13} /> : null}</i>
      {label}
    </span>
  );
}

function ApplicationStatus({ application }) {
  const { profile, user } = application;
  const content = {
    submitted: {
      icon: Send,
      eyebrow: "Application received",
      title: "Your profile is in the review queue.",
      text: "The PhotoX Admin team will verify your professional details and portfolio before activating partner access.",
    },
    under_review: {
      icon: FileCheck2,
      eyebrow: "Review in progress",
      title: "We are reviewing your application.",
      text: "Your application is locked while the Admin team reviews it. You will see the decision here after completion.",
    },
    suspended: {
      icon: ShieldCheck,
      eyebrow: "Partner access suspended",
      title: "Your Photographer workspace is temporarily unavailable.",
      text: "Review the Admin note below. Partner access can be restored after the required information is verified.",
    },
  }[profile.status];
  const Icon = content?.icon || FileCheck2;
  return (
    <section className="partner-status-card">
      <div className={`partner-status-icon status-${profile.status}`}>
        <Icon />
      </div>
      <p className="eyebrow">{content?.eyebrow}</p>
      <h1>{content?.title}</h1>
      <p>{content?.text}</p>
      <span className={`partner-status-pill status-${profile.status}`}>
        {profile.status.replace(/_/g, " ")}
      </span>
      {profile.reviewNote && (
        <div className="partner-feedback">
          <strong>Admin note</strong>
          <p>{profile.reviewNote}</p>
        </div>
      )}
      <dl className="partner-summary">
        <div>
          <dt>Applicant</dt>
          <dd>{user.displayName}</dd>
        </div>
        <div>
          <dt>Studio</dt>
          <dd>{profile.businessName}</dd>
        </div>
        <div>
          <dt>Application version</dt>
          <dd>{profile.applicationVersion}</dd>
        </div>
        <div>
          <dt>Submitted</dt>
          <dd>{formatDate(profile.submittedAt)}</dd>
        </div>
      </dl>
    </section>
  );
}

function ApplicationForm({ application, busy, saveDraft, submit }) {
  const profile = application.profile || {};
  const [form, setForm] = useState(() => ({
    displayName: application.user.displayName || "",
    phone: application.user.phone || "",
    businessName: profile.businessName || "",
    headline: profile.headline || "",
    bio: profile.bio || "",
    experienceYears: profile.experienceYears ?? "",
    primaryCity: profile.primaryCity || "",
    serviceRadiusKm: profile.serviceRadiusKm ?? "",
    specialtyCodes: profile.specialtyCodes || [],
    portfolioUrls: (profile.portfolioUrls || []).join("\n"),
    equipment: (profile.equipment || []).join(", "),
    languages: (profile.languages || []).join(", "),
    minimum: profile.pricingRange?.minimum ?? "",
    maximum: profile.pricingRange?.maximum ?? "",
    currency: profile.pricingRange?.currency || "INR",
  }));
  const [declaration, setDeclaration] = useState(false);
  const rejected = profile.status === "rejected";

  const set = (field) => (value) =>
    setForm((current) => ({ ...current, [field]: value }));
  const toggleSpecialty = (code) =>
    setForm((current) => ({
      ...current,
      specialtyCodes: current.specialtyCodes.includes(code)
        ? current.specialtyCodes.filter((item) => item !== code)
        : [...current.specialtyCodes, code],
    }));
  const values = () => ({
    displayName: form.displayName,
    phone: form.phone,
    businessName: form.businessName,
    headline: form.headline,
    bio: form.bio,
    ...(form.experienceYears !== ""
      ? { experienceYears: Number(form.experienceYears) }
      : {}),
    primaryCity: form.primaryCity,
    ...(form.serviceRadiusKm !== ""
      ? { serviceRadiusKm: Number(form.serviceRadiusKm) }
      : {}),
    specialtyCodes: form.specialtyCodes,
    portfolioUrls: lines(form.portfolioUrls),
    equipment: commaList(form.equipment),
    languages: commaList(form.languages),
    pricingRange: {
      ...(form.minimum !== "" ? { minimum: Number(form.minimum) } : {}),
      ...(form.maximum !== "" ? { maximum: Number(form.maximum) } : {}),
      currency: form.currency,
    },
  });

  return (
    <form
      className="partner-application"
      onSubmit={(event) => {
        event.preventDefault();
        if (declaration) submit(values());
      }}
    >
      <div className="partner-form-heading">
        <div>
          <p className="eyebrow">
            {rejected ? "Revise and resubmit" : "Partner application"}
          </p>
          <h1>Tell us about your photography work.</h1>
          <p>
            Complete every required field before sending your profile for Admin
            approval.
          </p>
        </div>
        <span
          className={`partner-status-pill status-${profile.status || "draft"}`}
        >
          {(profile.status || "draft").replace(/_/g, " ")}
        </span>
      </div>
      {rejected && profile.reviewNote && (
        <div className="partner-feedback">
          <strong>Admin feedback</strong>
          <p>{profile.reviewNote}</p>
        </div>
      )}

      <FormSection
        icon={CircleUserRound}
        title="Contact details"
        text="How the PhotoX team will identify and contact you."
      >
        <div className="partner-field-grid two">
          <Field
            label="Full name *"
            value={form.displayName}
            onChange={set("displayName")}
            required
            autoComplete="name"
          />
          <Field
            label="Phone number *"
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            required
            autoComplete="tel"
            placeholder="+91 90000 00000"
          />
        </div>
        <div className="partner-readonly">
          <span>Email address</span>
          <strong>{application.user.email}</strong>
          <small>Verified through email OTP</small>
        </div>
      </FormSection>

      <FormSection
        icon={BriefcaseBusiness}
        title="Professional profile"
        text="Present your studio and experience clearly to the review team."
      >
        <div className="partner-field-grid two">
          <Field
            label="Business or studio name *"
            value={form.businessName}
            onChange={set("businessName")}
            required
          />
          <Field
            label="Years of experience *"
            type="number"
            min="0"
            max="80"
            value={form.experienceYears}
            onChange={set("experienceYears")}
            required
          />
        </div>
        <Field
          label="Professional headline *"
          value={form.headline}
          onChange={set("headline")}
          minLength="10"
          maxLength="180"
          required
          placeholder="Wedding and celebration photographer"
        />
        <TextArea
          label="Professional biography *"
          value={form.bio}
          onChange={set("bio")}
          minLength="50"
          maxLength="3000"
          required
          hint={`${form.bio.length}/3000 · minimum 50 characters`}
        />
      </FormSection>

      <FormSection
        icon={MapPin}
        title="Service area"
        text="Help Admin match you with relevant events."
      >
        <div className="partner-field-grid two">
          <Field
            label="Primary city *"
            value={form.primaryCity}
            onChange={set("primaryCity")}
            required
          />
          <Field
            label="Service radius (km) *"
            type="number"
            min="0"
            max="5000"
            value={form.serviceRadiusKm}
            onChange={set("serviceRadiusKm")}
            required
          />
        </div>
      </FormSection>

      <FormSection
        icon={Camera}
        title="Specialties and portfolio"
        text="Choose your strongest event categories and provide real work samples."
      >
        <fieldset className="partner-specialties">
          <legend>Photography specialties *</legend>
          <div>
            {specialties.map(([code, label]) => (
              <label
                key={code}
                className={form.specialtyCodes.includes(code) ? "selected" : ""}
              >
                <input
                  type="checkbox"
                  checked={form.specialtyCodes.includes(code)}
                  onChange={() => toggleSpecialty(code)}
                />
                <CheckCircle2 size={17} />
                {label}
              </label>
            ))}
          </div>
        </fieldset>
        <TextArea
          label="Portfolio URLs *"
          value={form.portfolioUrls}
          onChange={set("portfolioUrls")}
          required
          placeholder={
            "https://yourportfolio.com/weddings\nhttps://instagram.com/yourstudio"
          }
          hint="Enter one HTTP or HTTPS URL per line."
        />
        <Field
          label="Camera and lighting equipment"
          value={form.equipment}
          onChange={set("equipment")}
          placeholder="Sony A7 IV, 24–70mm f/2.8, Godox AD200"
          hint="Separate items with commas."
        />
      </FormSection>

      <FormSection
        icon={Languages}
        title="Languages and pricing"
        text="Provide an indicative range; final event pricing can be agreed separately."
      >
        <Field
          label="Languages spoken *"
          value={form.languages}
          onChange={set("languages")}
          required
          placeholder="English, Hindi, Telugu"
          hint="Separate languages with commas."
        />
        <div className="partner-field-grid three">
          <Field
            label="Minimum price *"
            type="number"
            min="0"
            value={form.minimum}
            onChange={set("minimum")}
            required
          />
          <Field
            label="Maximum price *"
            type="number"
            min="0"
            value={form.maximum}
            onChange={set("maximum")}
            required
          />
          <label className="partner-field">
            Currency *
            <select
              value={form.currency}
              onChange={(event) => set("currency")(event.target.value)}
            >
              <option value="INR">INR</option>
              <option value="USD">USD</option>
              <option value="AED">AED</option>
            </select>
          </label>
        </div>
      </FormSection>

      <label className="partner-declaration">
        <input
          type="checkbox"
          checked={declaration}
          onChange={(event) => setDeclaration(event.target.checked)}
        />
        <span>
          <strong>
            I confirm that this information and portfolio are accurate.
          </strong>
          <small>
            I understand that PhotoX may reject or suspend profiles that contain
            misleading information.
          </small>
        </span>
      </label>
      <div className="partner-form-actions">
        <button
          type="button"
          className="button button-light"
          disabled={busy}
          onClick={() => saveDraft(values())}
        >
          {busy ? (
            <LoaderCircle className="spin" size={17} />
          ) : (
            <FileCheck2 size={17} />
          )}{" "}
          Save draft
        </button>
        <button
          className="button button-primary"
          disabled={busy || !declaration}
        >
          {busy ? (
            <LoaderCircle className="spin" size={17} />
          ) : (
            <Send size={17} />
          )}
          {rejected ? "Resubmit application" : "Submit for review"}
        </button>
      </div>
    </form>
  );
}

function FormSection({ icon: Icon, title, text, children }) {
  return (
    <section className="partner-form-section">
      <header>
        <span>
          <Icon size={20} />
        </span>
        <div>
          <h2>{title}</h2>
          <p>{text}</p>
        </div>
      </header>
      <div className="partner-form-content">{children}</div>
    </section>
  );
}

function Field({ label, value, onChange, hint, ...properties }) {
  return (
    <label className="partner-field">
      {label}
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        {...properties}
      />
      {hint && <small>{hint}</small>}
    </label>
  );
}

function TextArea({ label, value, onChange, hint, ...properties }) {
  return (
    <label className="partner-field">
      {label}
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        {...properties}
      />
      {hint && <small>{hint}</small>}
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="partner-field">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map(([optionValue, text]) => (
          <option key={optionValue} value={optionValue}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );
}

function Workspace({ workspace, logout, refresh }) {
  const [view, setViewState] = useState(() => getPhotographerViewFromUrl());
  const setView = useCallback((nextView) => {
    if (navigatePhotographerView(nextView)) setViewState(nextView);
  }, []);
  useEffect(() => {
    const restoreView = () => setViewState(getPhotographerViewFromUrl());
    window.addEventListener("popstate", restoreView);
    return () => window.removeEventListener("popstate", restoreView);
  }, []);
  const assignedEvents = workspace.events.filter(
    (event) => event.photographerAccess !== "open",
  );
  const openEvents = workspace.events.filter(
    (event) => event.photographerAccess === "open",
  );
  const [eventId, setEventId] = useState(assignedEvents[0]?.id || "");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const studio = workspace.memberships.studios.find(
    (membership) => membership.status === "active",
  )?.studio;

  const refreshGallery = useCallback(async () => {
    if (!eventId) return setPhotos([]);
    try {
      setPhotos(await loadGallery(eventId, "all"));
    } catch (requestError) {
      setError(requestError.message);
    }
  }, [eventId]);
  useEffect(() => {
    refreshGallery();
  }, [refreshGallery]);

  async function createAccess(id) {
    setBusy(true);
    setError("");
    try {
      const access = await rotateEventAccess(id);
      await navigator.clipboard?.writeText(access.entryUrl);
      setNotice(`Guest access created: ${access.entryUrl}`);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function register(event) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await registerForEvent(event.id);
      setNotice("Registration sent. Admin can now approve you for this event.");
      await refresh();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="studio-app">
      <aside className="sidebar">
        <Brand light />
        <div className="studio-card">
          <small>Approved partner</small>
          <strong>{workspace.user.displayName || workspace.user.email}</strong>
          <span>{studio?.name || "No active studio"}</span>
        </div>
        <nav>
          {navigation.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={view === id ? "active" : ""}
              onClick={() => setView(id)}
            >
              <Icon size={18} />
              <span>{label}</span>
            </button>
          ))}
        </nav>
        <button className="partner-workspace-logout" onClick={logout}>
          <LogOut size={17} /> Sign out
        </button>
      </aside>
      <section className="studio-main">
        <header className="topbar">
          <button className="mobile-logo" aria-label="PhotoX">
            <Image size={19} />
          </button>
          <span className="muted">Photographer workspace</span>
          <span className="live-sync">
            <Radio size={13} /> Live · updates every 15 seconds
          </span>
          <button className="button button-light" onClick={refresh}>
            <RefreshCw size={15} /> Refresh events
          </button>
          <BadgeCheck className="partner-approved-mark" size={20} />
        </header>
        <div className="content">
          {error && <Notice error text={error} />}
          {notice && <Notice text={notice} />}
          {view === "overview" && (
            <SimpleOverview
              assignedEvents={assignedEvents}
              openEvents={openEvents}
              studio={studio}
              user={workspace.user}
              onNavigate={setView}
            />
          )}
          {view === "events" && (
            <Events
              assignedEvents={assignedEvents}
              openEvents={openEvents}
              busy={busy}
              open={(id) => {
                setEventId(id);
                setView("gallery");
              }}
              createAccess={createAccess}
              register={register}
            />
          )}
          {view === "uploads" && (
            <Uploads
              events={assignedEvents}
              eventId={eventId}
              setEventId={setEventId}
              onUploaded={refreshGallery}
            />
          )}
          {view === "gallery" && (
            <Gallery
              events={assignedEvents}
              eventId={eventId}
              setEventId={setEventId}
              photos={photos}
              onRefresh={refreshGallery}
            />
          )}
          {view === "earnings" && (
            <FixedEarnings events={assignedEvents} />
          )}
        </div>
      </section>
      <nav
        className="studio-mobile-nav"
        aria-label="Photographer mobile navigation"
      >
        {navigation.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={view === id ? "active" : ""}
            aria-current={view === id ? "page" : undefined}
            onClick={() => setView(id)}
          >
            <Icon size={19} />
            <span>{label}</span>
          </button>
        ))}
      </nav>
    </main>
  );
}

function PageHeading({ eyebrow, title, children }) {
  return (
    <div className="page-heading">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {children}
      </div>
    </div>
  );
}
function SimpleOverview({ assignedEvents, openEvents, studio, user, onNavigate }) {
  const firstName = (user?.displayName || "Partner").split(/\s+/)[0];
  const scheduled = assignedEvents.reduce(
    (total, event) => total + (event.commercial?.photographerShareMinor || 0),
    0,
  );
  const currency = assignedEvents.find((event) => event.commercial)?.commercial
    ?.currency || "INR";
  return (
    <div className="partner-overview">
      <section className="partner-overview-hero"><div>
        <span className="partner-live-pill"><BadgeCheck size={14} /> Approved partner · {studio?.name || "Independent"}</span>
        <p className="eyebrow">Photographer workspace</p>
        <h1>Welcome back, {firstName}.</h1>
        <p>Register for paid events, wait for Admin approval, and manage assigned events. Every price is fixed by the client’s plan.</p>
        <div className="partner-hero-actions">
          <button className="button button-primary" onClick={() => onNavigate("events")}><BriefcaseBusiness size={16} /> View events</button>
          <button className="button partner-ghost-light" onClick={() => onNavigate("earnings")}>View fixed earnings <ArrowRight size={16} /></button>
        </div>
      </div></section>
      <div className="metrics">
        <Metric icon={CalendarDays} label="Assigned events" value={assignedEvents.length} />
        <Metric icon={BriefcaseBusiness} label="Open registrations" value={openEvents.length} hint="Admin approves registrations" tone="teal" />
        <Metric icon={CircleDollarSign} label="Scheduled 85% share" value={formatMoney(scheduled, currency)} hint="Based only on paid plan prices" tone="gold" />
      </div>
    </div>
  );
}

function FixedEarnings({ events = [] }) {
  const paidEvents = events.filter((event) => event.commercial);
  return (
    <>
      <PageHeading eyebrow="Fixed plan commission" title="Earnings">
        <p>The client pays once for the selected event plan. You receive 85% and PhotoX retains 15%, with no separate photographer checkout.</p>
      </PageHeading>
      <section className="panel"><h2>Assigned event earnings</h2>
        {paidEvents.length ? paidEvents.map((event) => (
          <article key={event.id} className="event-card-body"><h3>{event.name}</h3><p>
            Plan total: {formatMoney(event.commercial.totalAmountMinor, event.commercial.currency)} · Your share: {formatMoney(event.commercial.photographerShareMinor, event.commercial.currency)} (85%) · PhotoX: {formatMoney(event.commercial.platformCommissionMinor, event.commercial.currency)} (15%)
          </p></article>
        )) : <Empty text="No paid events have been assigned yet." />}
      </section>
    </>
  );
}
function Metric({ icon: Icon, label, value, hint, tone = "coral" }) {
  return (
    <article className="metric">
      <span className={`metric-icon ${tone}`}>
        <Icon />
      </span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
        {hint && <em>{hint}</em>}
      </div>
    </article>
  );
}

function Events({ assignedEvents, openEvents, busy, open, createAccess, register }) {
  return (
    <>
      <PageHeading eyebrow="Admin-assigned work" title="Events">
        <p>
          Work on events assigned to you or register for a paid open event.
          Admin makes the final photographer assignment.
        </p>
      </PageHeading>
      <h2>Assigned to you</h2>
      {assignedEvents.length ? (
        <div className="event-cards">
          {assignedEvents.map((event) => (
            <article key={event.id}>
              <div className="event-cover empty-media">
                <CalendarDays size={35} />
                <span className="status status-draft">{event.status}</span>
              </div>
              <div className="event-card-body">
                <h3>{event.name}</h3>
                <small>{formatDate(event.startsAt)}</small>
                <footer>
                  <button
                    className="button button-light"
                    onClick={() => open(event.id)}
                  >
                    Open gallery
                  </button>
                  <button
                    className="button button-dark"
                    disabled={busy}
                    onClick={() => createAccess(event.id)}
                  >
                    Create guest link
                  </button>
                </footer>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <Empty text="No events are assigned to this account." />
      )}
      <h2>Open assignments</h2>
      {openEvents.length ? (
        <div className="event-cards">
          {openEvents.map((event) => (
            <article key={event.id}>
              <div className="event-cover empty-media">
                <BriefcaseBusiness size={35} />
                <span className="status status-draft">unassigned</span>
              </div>
              <div className="event-card-body">
                <h3>{event.name}</h3>
                <small>
                  {event.eventType?.name || "Event"} ·{" "}
                  {formatDate(event.startsAt)}
                </small>
                <p>{event.venue?.city || "Venue pending"}</p>
                {event.commercial && (
                  <p>
                    Fixed plan: {formatMoney(event.commercial.totalAmountMinor, event.commercial.currency)}
                    {" · "}Your 85% share: {formatMoney(event.commercial.photographerShareMinor, event.commercial.currency)}
                  </p>
                )}
                <footer>
                  {event.applicationStatus === "pending" ? (
                    <span>Registration awaiting Admin approval</span>
                  ) : event.applicationStatus === "rejected" ? (
                    <button className="button button-light" disabled={busy} onClick={() => register(event)}>
                      Register again
                    </button>
                  ) : (
                    <button className="button button-dark" disabled={busy} onClick={() => register(event)}>
                      Register for event
                    </button>
                  )}
                </footer>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <Empty text="No paid events are currently open for photographer registration." />
      )}
    </>
  );
}

function Uploads({ events, eventId, setEventId, onUploaded }) {
  return (
    <>
      <PageHeading eyebrow="Media workflow" title="Professional bulk uploads">
        <p>
          Select an event, upload multiple photographs, or import from Google
          Drive. Originals remain in private object storage.
        </p>
      </PageHeading>
      <section className="panel upload-panel">
        <label>
          Event{" "}
          <select
            value={eventId}
            onChange={(event) => setEventId(event.target.value)}
          >
            <option value="">Select an event</option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>
                {event.name}
              </option>
            ))}
          </select>
        </label>
        <BulkMediaUploader
          eventId={eventId}
          api={photographerMediaApi}
          onUploaded={onUploaded}
        />
      </section>
    </>
  );
}
function Gallery({ events, eventId, setEventId, photos, onRefresh }) {
  return (
    <>
      <PageHeading eyebrow="Media management" title="Event gallery">
        <p>
          Search, filter and inspect professional and explicitly shared event
          photos. Private guest media is visible only to authorized event
          managers and the uploader.
        </p>
      </PageHeading>
      <label className="partner-gallery-event">
        Event{" "}
        <select
          value={eventId}
          onChange={(event) => setEventId(event.target.value)}
        >
          <option value="">Select an event</option>
          {events.map((event) => (
            <option key={event.id} value={event.id}>
              {event.name}
            </option>
          ))}
        </select>
      </label>
      <PhotographerGalleryManager
        eventId={eventId}
        photos={photos}
        onRefresh={onRefresh}
        getOriginal={getPhotographerPhotoDelivery}
      />
    </>
  );
}

function Brand({ light = false }) {
  return (
    <div className={`partner-brand ${light ? "light" : ""}`}>
      <span>
        <Image size={19} />
      </span>
      <strong>
        Photo<span>X</span>
      </strong>
    </div>
  );
}
function Notice({ text, error = false }) {
  return (
    <div
      className={`panel notice ${error ? "notice-error" : ""}`}
      role={error ? "alert" : "status"}
    >
      {text}
    </div>
  );
}
function Empty({ text }) {
  return <div className="panel empty-state">{text}</div>;
}
function Status({ icon: Icon, spin, title, message, action }) {
  return (
    <main className="status-page">
      <Icon className={spin ? "spin" : ""} />
      <h1>{title}</h1>
      {message && <p>{message}</p>}
      {action && (
        <button className="button button-dark" onClick={action}>
          Retry
        </button>
      )}
    </main>
  );
}
function lines(value) {
  return value
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}
function isHttpUrl(value) {
  try {
    return ["http:", "https:"].includes(new URL(value).protocol);
  } catch {
    return false;
  }
}
function commaList(value) {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}
function formatDate(value) {
  return value
    ? new Intl.DateTimeFormat("en-IN", { dateStyle: "medium" }).format(
        new Date(value),
      )
    : "Not available";
}
function formatMoney(value, currency = "INR") {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency }).format(
    (value || 0) / 100,
  );
}

export { ApplicationForm, ApplicationStatus, AuthScreen, isHttpUrl };
export default App;
